import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getCampaignId } from '../api/client.js';

const ROLES = ['admin', 'coordinator', 'driver'];
const ENTITIES = [
  { key: 'client', label: 'Client', fields: ['phone', 'email', 'nb_units', 'contact_name', 'time_slot'] },
  { key: 'item', label: 'Item', fields: ['serial_number', 'secondary_id', 'scan_photo_url'] },
  { key: 'plan', label: 'Plan', fields: ['signature', 'delivery_notes', 'problem_note'] },
];

/**
 * Admin UI for configuring which fields are visible per role
 */
export default function FieldVisibilityConfig() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const cid = getCampaignId();

  const load = useCallback(async () => {
    try {
      const data = await apiFetch(`/c/${cid}/field-visibility`);
      const map = {};
      for (const row of data) {
        map[`${row.role}:${row.entity}:${row.field_name}`] = row.visible;
      }
      setConfig(map);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const getKey = (role, entity, field) => `${role}:${entity}:${field}`;
  const isVisible = (role, entity, field) => config[getKey(role, entity, field)] !== false;

  const toggle = (role, entity, field) => {
    const key = getKey(role, entity, field);
    setConfig(prev => ({ ...prev, [key]: !isVisible(role, entity, field) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const fields = [];
      for (const entity of ENTITIES) {
        for (const field of entity.fields) {
          for (const role of ROLES) {
            fields.push({
              role, entity: entity.key, fieldName: field,
              visible: isVisible(role, entity.key, field),
            });
          }
        }
      }
      await apiFetch(`/c/${cid}/field-visibility`, {
        method: 'PUT', body: JSON.stringify({ fields }),
      });
      setSuccess('Configuration sauvegardée');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-500 text-center py-8">Chargement...</p>;

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Visibilité des champs par rôle</h2>
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      {success && <p className="text-green-600 text-sm mb-3">{success}</p>}

      {ENTITIES.map((entity) => (
        <div key={entity.key} className="mb-6">
          <h3 className="font-semibold text-lg mb-2">{entity.label}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Champ</th>
                  {ROLES.map(r => <th key={r} className="p-3 text-center capitalize">{r}</th>)}
                </tr>
              </thead>
              <tbody>
                {entity.fields.map(field => (
                  <tr key={field} className="border-b last:border-0">
                    <td className="p-3 font-mono text-xs">{field}</td>
                    {ROLES.map(role => (
                      <td key={role} className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={isVisible(role, entity.key, field)}
                          onChange={() => toggle(role, entity.key, field)}
                          className="w-5 h-5 accent-blue-600"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Sauvegarde...' : 'Sauvegarder'}
      </button>
    </div>
  );
}
